import { createQueryClient, createWebApi } from '@care-platform/api-client';
import {
  DevelopmentAuthProvider,
  PasswordAuthProvider,
  UnconfiguredAuthProvider,
  useAuth,
  type AuthProvider,
} from '@care-platform/auth';
import { AuthGate, ShellProviders, buildShellRoutes, createRouteRegistry } from '@care-platform/shell';
import { useEffect, useState, type ReactNode } from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router';
import type { WebConfig } from './config';
import { HOME_ROUTE, mounts } from './modules';

const registry = createRouteRegistry(mounts);

/**
 * Lets the API client, created once, always reach the current session: the auth provider's functions change as
 * the session does, and the bridge is pointed at the latest one after each render.
 */
function createAuthBridge(initial: AuthProvider) {
  let current = initial;
  return {
    update: (next: AuthProvider) => {
      current = next;
    },
    getAccessToken: () => current.getAccessToken(),
    sessionRejected: () => current.sessionRejected(),
  };
}

function SignedInApp({ config }: { config: WebConfig }) {
  const auth = useAuth();
  const [bridge] = useState(() => createAuthBridge(auth));
  useEffect(() => bridge.update(auth), [bridge, auth]);

  const [queryClient] = useState(createQueryClient);
  const [api] = useState(() =>
    createWebApi({
      baseUrl: config.apiBaseUrl,
      getAccessToken: bridge.getAccessToken,
      onUnauthorized: () => {
        queryClient.clear();
        bridge.sessionRejected();
      },
    }),
  );
  const [router] = useState(() =>
    createBrowserRouter(buildShellRoutes(registry, HOME_ROUTE), { basename: import.meta.env.BASE_URL }),
  );

  return (
    <ShellProviders api={api} queryClient={queryClient} registry={registry}>
      <RouterProvider router={router} />
    </ShellProviders>
  );
}

function AuthProvider({ config, children }: { config: WebConfig; children: ReactNode }) {
  if (config.authMethod === 'development') {
    return (
      <DevelopmentAuthProvider initialToken={config.developmentToken}>{children}</DevelopmentAuthProvider>
    );
  }
  if (config.authMethod === 'password') {
    // Placeholder sign-in for Development until the identity provider (P1): the BFF's dev-identity endpoint.
    return (
      <PasswordAuthProvider tokenUrl={`${config.apiBaseUrl}/dev-identity/token`}>
        {children}
      </PasswordAuthProvider>
    );
  }
  return <UnconfiguredAuthProvider>{children}</UnconfiguredAuthProvider>;
}

/** The Web MMS shell: sign-in, then the composed modules. */
export function App({ config }: { config: WebConfig }) {
  return (
    <AuthProvider config={config}>
      <AuthGate>
        <SignedInApp config={config} />
      </AuthGate>
    </AuthProvider>
  );
}
